import { createContext, useContext } from "react";

import type { BrowserPushSubscription } from "@/features/storefront/api/storefront";

/**
 * Push notifications, the browser half.
 *
 * The server decides whether this shop offers push at all and says so by
 * handing over a public key (`pwa.push_public_key`); the layout passes it down
 * only where the service worker is registered, because a push arrives through
 * that worker and nowhere else. Everything here assumes that and asks only the
 * questions the browser can answer: does it support push, has the shopper
 * already said no, and what is this browser's subscription.
 */

export interface StorefrontPush {
  /** The VAPID key, base64url, as the server sent it. */
  publicKey: string;
  /** The service worker's scope - the registration push arrives through. */
  scope: string;
}

export const StorefrontPushContext = createContext<StorefrontPush | null>(null);

/** This shop's push offer, or `null` where it makes none. */
export function useStorefrontPush(): StorefrontPush | null {
  return useContext(StorefrontPushContext);
}

export type PushPermission = "unsupported" | "default" | "granted" | "denied";

/**
 * What the browser will let us do. iOS only exposes push to a shop installed
 * on the home screen, so a Safari tab there reads as unsupported - which is
 * the truth for that tab.
 */
export function pushPermission(): PushPermission {
  if (
    typeof window === "undefined" ||
    !window.isSecureContext ||
    !("serviceWorker" in navigator) ||
    !("PushManager" in window) ||
    !("Notification" in window)
  ) {
    return "unsupported";
  }
  return Notification.permission;
}

/** `applicationServerKey` wants bytes; the server sends base64url. */
export function base64UrlToBytes(value: string): Uint8Array<ArrayBuffer> {
  const padded =
    value.replace(/-/g, "+").replace(/_/g, "/") +
    "=".repeat((4 - (value.length % 4)) % 4);
  const raw = atob(padded);
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let index = 0; index < raw.length; index += 1)
    bytes[index] = raw.charCodeAt(index);
  return bytes;
}

function bytesToBase64Url(buffer: ArrayBuffer | null): string {
  if (!buffer) return "";
  let raw = "";
  for (const byte of new Uint8Array(buffer)) raw += String.fromCharCode(byte);
  return btoa(raw).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function sameKey(subscription: PushSubscription, publicKey: string): boolean {
  const current = bytesToBase64Url(subscription.options.applicationServerKey);
  // A browser that does not report the key cannot be compared; keep it.
  return !current || current === publicKey.replace(/=+$/, "");
}

async function registration(
  scope: string,
): Promise<ServiceWorkerRegistration | null> {
  const found = await navigator.serviceWorker.getRegistration(scope);
  if (!found) return null;
  // Registered but not yet active on a first visit: subscribing needs an
  // active worker, so wait for it rather than failing the tap.
  if (!found.active) await navigator.serviceWorker.ready;
  return found;
}

/** This browser's current subscription for the shop, without asking anything. */
export async function currentSubscription(
  push: StorefrontPush,
): Promise<PushSubscription | null> {
  if (pushPermission() !== "granted") return null;
  const found = await registration(push.scope);
  const subscription = (await found?.pushManager.getSubscription()) ?? null;
  return subscription && sameKey(subscription, push.publicKey)
    ? subscription
    : null;
}

/**
 * Ask for permission (the browser's own prompt, so only ever from a tap) and
 * subscribe. `null` when the shopper said no or the browser could not.
 *
 * A subscription made under a different key - the platform rotated it - is
 * replaced, because the server can no longer send to it.
 */
export async function subscribeBrowser(
  push: StorefrontPush,
): Promise<BrowserPushSubscription | null> {
  if (pushPermission() === "unsupported") return null;
  const permission = await Notification.requestPermission();
  if (permission !== "granted") return null;

  const found = await registration(push.scope);
  if (!found) return null;
  let subscription = await found.pushManager.getSubscription();
  if (subscription && !sameKey(subscription, push.publicKey)) {
    await subscription.unsubscribe();
    subscription = null;
  }
  subscription ??= await found.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: base64UrlToBytes(push.publicKey),
  });
  return toServerShape(subscription);
}

export function toServerShape(
  subscription: PushSubscription,
): BrowserPushSubscription | null {
  const json = subscription.toJSON();
  const p256dh = json.keys?.p256dh;
  const auth = json.keys?.auth;
  if (!json.endpoint || !p256dh || !auth) return null;
  return { endpoint: json.endpoint, keys: { p256dh, auth } };
}

/*
 * "Yes, tell me about my orders on this device", remembered.
 *
 * Said once - after installing the shop, or on an order's confirmation page -
 * and every later order placed from this device is followed without asking
 * again: the shopper already answered the question, for this device. Turning
 * notifications off forgets it. Kept per scope, because outside production one
 * origin serves every shop.
 *
 * Browser storage can be missing or refused (private windows, blocked site
 * data); then nothing is remembered and the shopper is simply offered again.
 */
const OPT_IN_KEY = "storefront-push-opt-in";
const DISMISSED_KEY = "storefront-push-offer-dismissed";

function read(key: string, scope: string): boolean {
  try {
    return window.localStorage.getItem(`${key}:${scope}`) === "1";
  } catch {
    return false;
  }
}

function write(key: string, scope: string, value: boolean): void {
  try {
    if (value) window.localStorage.setItem(`${key}:${scope}`, "1");
    else window.localStorage.removeItem(`${key}:${scope}`);
  } catch {
    // Not remembered; the shopper is offered again next time.
  }
}

/** Whether the shopper said yes to order updates on this device. */
export function hasDeviceOptIn(push: StorefrontPush): boolean {
  return read(OPT_IN_KEY, push.scope);
}

export function setDeviceOptIn(push: StorefrontPush, value: boolean): void {
  write(OPT_IN_KEY, push.scope, value);
}

/** Whether "Not now" was pressed on the after-install offer. */
export function wasInstallOfferDismissed(push: StorefrontPush): boolean {
  return read(DISMISSED_KEY, push.scope);
}

export function dismissInstallOffer(push: StorefrontPush): void {
  write(DISMISSED_KEY, push.scope, true);
}
